import {
  generateClassName,
} from '@klv/shared/utils/functions';
import { useTranslation } from '@klv/hooks/useTranslation';
import { useState } from 'react';
import type { FocusEvent } from 'react';
import * as Yup from 'yup';
import { hasStorageAvailable } from '@klv/shared/utils/storage';
import RoundedText from '@klv/molecules/form-field/rounded-text';
import { gtmFormEvent } from '@klv/shared/utils/google-tag-manager';
import Button from '@klv/atoms/button';
import Buttons from '@klv/molecules/buttons';
import { Formik, Form } from 'formik';
import type { FormikProps } from 'formik';
import type { IFormValidationSchema, IInitialFormValues, ISingleLineFormState } from '../../organisms/form/types';
import type { ISingleLineForm } from './types';
import * as styles from './styles/singleLineForm.module.scss';

const initialValues: ISingleLineFormState = {
  email: '',
};

// Custom onBlur handler that only updates form state without tracking
const handleSingleLineFormBlur = (
  event: FocusEvent,
  formik: FormikProps<IInitialFormValues>,
  setFormState: React.Dispatch<React.SetStateAction<ISingleLineFormState>>,
) => {
  const target = event.target as HTMLInputElement;
  formik.handleBlur(event);
  setFormState((prevState) => ({
    ...prevState,
    [target.name]: target.value,
  }));
};

export const EmailForm = ({
  primaryButtonText,
  buttons = [],
  placeholderText,
  backgroundColor = 'dark',
}: ISingleLineForm) => {

  const { t } = useTranslation();
  const [formState, setFormState] = useState(initialValues);
  const [submitted, setSubmitted] = useState(false);
  const locale = t('langCodes_pathPrefix') || '';
  const signUpPath = locale === '' ? '/sign-up' : `/${locale}/sign-up`;

  const validationSchemaBase: IFormValidationSchema = {
    email: Yup.string()
      .email(`${t('atoms_singleLineForm_error')}`)
      .required(`${t('organisms_forms_fields_email_errors_required')}`),
  };

  const initialVals: IInitialFormValues = { email: formState.email };

  return (
    <div className={styles.singleLineFormContainer}>
      <Formik
        initialValues={initialVals}
        validationSchema={Yup.object(validationSchemaBase)}
        validateOnBlur={true}
        onSubmit={async (values) => {
          if (hasStorageAvailable('sessionStorage')) {
            sessionStorage.setItem('formValues', JSON.stringify({ email: values.email }));
          }
          gtmFormEvent(values, 'singleLineForm', 'submit').then(() => {
            window.location.assign(signUpPath);
          });
        }}
      >
        {(formik) => {
          const hasEmailError = submitted && formik.touched.email && formik.errors.email;

          return (
            <div
              className={generateClassName([
                styles.inputAndButtonContainer,
                hasEmailError && styles.errorBackground,
              ])}
            >
              <Form className={styles.formContainer} id="form--demoRequest">
                <RoundedText
                  backgroundColor={backgroundColor}
                  id="singleLineForm-email"
                  name="email"
                  type="email"
                  submitted={submitted}
                  placeholder={placeholderText || `${t('atoms_singleLineForm_placeholderText')}`}
                  onBlur={(event: FocusEvent) => handleSingleLineFormBlur(event, formik, setFormState)}
                />
                <Button
                  className={styles.getStartedButton}
                  text={primaryButtonText || `${t('atoms_singleLineForm_primaryButton')}`}
                  buttonColor='dark'
                  type="submit"
                  onFocus={() => {
                    setSubmitted(true);
                  }}
                />
              </Form>
            </div>
          );
        }}
      </Formik>

      <Buttons
        buttonColor='dark'
        variants={['tertiary', 'tertiary']}
        buttons={buttons}
      />
    </div>
  );
};
